import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Plus, Pencil, Trash2, FileText, Save, GripVertical, 
  MessageSquare, Star, Hash, List, Type, ArrowUp, ArrowDown,
  ClipboardList, Settings, Upload, Eye, Copy, MoreVertical, CheckSquare
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

const TIPOS_PREGUNTA = [
  { id: "nps", nombre: "NPS (0-10)", icono: Hash, descripcion: "Pregunta Net Promoter Score clásica" },
  { id: "satisfaccion", nombre: "Satisfacción (1-5)", icono: Star, descripcion: "Escala de estrellas" },
  { id: "texto", nombre: "Texto libre", icono: Type, descripcion: "Respuesta abierta" },
  { id: "opcion_multiple", nombre: "Opción única", icono: List, descripcion: "Selección de una sola opción" },
  { id: "seleccion_multiple", nombre: "Selección múltiple", icono: CheckSquare, descripcion: "Selección de varias opciones (checkboxes)" },
  { id: "escala", nombre: "Escala personalizada", icono: Hash, descripcion: "Escala numérica configurable" }
];

const TIPOS_ENCUESTA = [
  { id: "general", nombre: "Servicio General" },
  { id: "post_siniestro", nombre: "Post-Siniestro" },
  { id: "post_reclamo", nombre: "Post-Reclamo" },
  { id: "personalizada", nombre: "Personalizada" }
];

const CAMPOS_FECHA = [
  { value: "renovacion", label: "Mes de renovación a encuestar" },
  { value: "reclamo", label: "Mes del reclamo encuestado" },
  { value: "ninguno", label: "Ninguno" },
];
const labelCampoFecha = (v) => CAMPOS_FECHA.find((c) => c.value === v)?.label || "Ninguno";

export default function EncuestasConfigPage() {
  const { getAuthHeaders, user } = useAuth();
  const [plantillas, setPlantillas] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingPreguntas, setUploadingPreguntas] = useState(false);
  const [uploadingImagenBienvenida, setUploadingImagenBienvenida] = useState(false);

  // Configuración de tipos de encuesta por departamento
  const [tiposConfig, setTiposConfig] = useState([]);
  const [activePageTab, setActivePageTab] = useState("plantillas");
  const [selectedTipoDept, setSelectedTipoDept] = useState("");
  const [tiposDraft, setTiposDraft] = useState([]);
  const [nuevoTipo, setNuevoTipo] = useState("");
  const [savingTipos, setSavingTipos] = useState(false);

  // Tipos de Encuesta globales (con campo de fecha asociado)
  const [tiposEncuesta, setTiposEncuesta] = useState([]);
  const [nuevoTipoEncNombre, setNuevoTipoEncNombre] = useState("");
  const [nuevoTipoEncCampo, setNuevoTipoEncCampo] = useState("ninguno");
  const [savingTipoEnc, setSavingTipoEnc] = useState(false);

  const [subdepartamentos, setSubdepartamentos] = useState([]);
  const [selectedSubDeptDept, setSelectedSubDeptDept] = useState("");
  const [nuevoSubDept, setNuevoSubDept] = useState("");
  const [savingSubDept, setSavingSubDept] = useState(false);
  
  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    tipo: "",
    departamento_id: "",
    departamento_nombre: "",
    sub_departamento: "", // Para RRGG: "Auto" o "Propiedades"
    mensaje_bienvenida: "Nos gustaría conocer su opinión sobre nuestro servicio.",
    mensaje_agradecimiento: "¡Gracias por su tiempo! Sus comentarios son muy valiosos para nosotros.",
    asunto_correo: "",
    mensaje_cuerpo_correo: "",
    preguntas: [],
    activa: true,
    logo_url: "",
    imagen_bienvenida_url: "",
    usar_imagen_bienvenida: false,
    color_primario: "#0C314C",
    color_secundario: "#45E69A",
    mostrar_logo_empresa: true,
    header_url: "",
    footer_url: "",
    color_texto_encabezado: "#FFFFFF",
    color_texto_cuerpo: "#374151",
    color_texto_footer: "#9ca3af",
    logo_alineacion: "centro",
    logo_tamano: "150"
  });
  
  const [showPreguntaDialog, setShowPreguntaDialog] = useState(false);
  const [editingPregunta, setEditingPregunta] = useState(null);
  const [preguntaForm, setPreguntaForm] = useState({
    tipo: "nps",
    pregunta: "",
    opciones: [],
    escala_min: 1,
    escala_max: 10,
    obligatoria: true
  });
  const [nuevaOpcion, setNuevaOpcion] = useState("");

  const [emailPreviewHtml, setEmailPreviewHtml] = useState("");
  const [emailPreviewAsunto, setEmailPreviewAsunto] = useState("");
  const [loadingEmailPreview, setLoadingEmailPreview] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  const buildEmailPreviewPayload = () => ({
    nombre: formData.nombre,
    logo_url: formData.logo_url || null,
    mostrar_logo_empresa: formData.mostrar_logo_empresa,
    color_primario: formData.color_primario,
    color_secundario: formData.color_secundario,
    mensaje_bienvenida: formData.mensaje_bienvenida,
    asunto_correo: formData.asunto_correo || null,
    mensaje_cuerpo_correo: formData.mensaje_cuerpo_correo || null,
    imagen_bienvenida_url: formData.imagen_bienvenida_url || null,
    usar_imagen_bienvenida: formData.usar_imagen_bienvenida,
    header_url: formData.header_url || null,
    footer_url: formData.footer_url || null,
    color_texto_encabezado: formData.color_texto_encabezado,
    color_texto_cuerpo: formData.color_texto_cuerpo,
    color_texto_footer: formData.color_texto_footer,
    logo_alineacion: formData.logo_alineacion,
    logo_tamano: formData.logo_tamano
  });

  const handleEnviarCorreoPrueba = async () => {
    const destino = (testEmail || "").trim();
    if (!destino) {
      toast.error("Ingrese un correo de destino");
      return;
    }
    setSendingTestEmail(true);
    try {
      const res = await axios.post(
        `${API}/encuestas/plantillas/preview-email/enviar-prueba`,
        { ...buildEmailPreviewPayload(), email_destino: destino },
        getAuthHeaders()
      );
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar el correo de prueba");
    } finally {
      setSendingTestEmail(false);
    }
  };

  useEffect(() => {
    if (showDialog && !testEmail && user?.email) {
      setTestEmail(user.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDialog]);

  useEffect(() => {
    fetchPlantillas();
    fetchDepartamentos();
    fetchTiposConfig();
    fetchTiposEncuesta();
    fetchSubdepartamentos();
  }, []);

  useEffect(() => {
    if (!showDialog) return;
    const timer = setTimeout(async () => {
      try {
        setLoadingEmailPreview(true);
        const res = await axios.post(`${API}/encuestas/plantillas/preview-email`, buildEmailPreviewPayload(), getAuthHeaders());
        setEmailPreviewHtml(res.data.html);
        setEmailPreviewAsunto(res.data.asunto);
      } catch (err) {
        console.error("Error loading email preview:", err);
      } finally {
        setLoadingEmailPreview(false);
      }
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDialog, formData.nombre, formData.logo_url, formData.mostrar_logo_empresa, formData.color_primario, formData.color_secundario, formData.mensaje_bienvenida, formData.asunto_correo, formData.mensaje_cuerpo_correo, formData.imagen_bienvenida_url, formData.usar_imagen_bienvenida, formData.header_url, formData.footer_url, formData.color_texto_encabezado, formData.color_texto_cuerpo, formData.color_texto_footer, formData.logo_alineacion, formData.logo_tamano]);

  const fetchDepartamentos = async () => {
    try {
      const res = await axios.get(`${API}/encuestas/departamentos`, getAuthHeaders());
      setDepartamentos(res.data);
    } catch (err) {
      console.error("Error loading departamentos:", err);
    }
  };

  const fetchTiposConfig = async () => {
    try {
      const res = await axios.get(`${API}/encuestas/tipos-departamento`, getAuthHeaders());
      setTiposConfig(res.data || []);
    } catch (err) {
      console.error("Error loading tipos-departamento:", err);
    }
  };

  const fetchTiposEncuesta = async () => {
    try {
      const res = await axios.get(`${API}/encuestas/tipos-encuesta`, getAuthHeaders());
      setTiposEncuesta(res.data || []);
    } catch (err) {
      console.error("Error loading tipos-encuesta:", err);
    }
  };

  const fetchSubdepartamentos = async () => {
    try {
      const res = await axios.get(`${API}/encuestas/subdepartamentos`, getAuthHeaders());
      setSubdepartamentos(res.data || []);
    } catch (err) {
      console.error("Error loading subdepartamentos:", err);
    }
  };

  const getSubdeptsForDept = (deptId) => subdepartamentos.filter((s) => s.departamento_id === deptId);

  const handleCrearSubDept = async () => {
    const nombre = nuevoSubDept.trim();
    if (!nombre) {
      toast.error("Ingrese el nombre del sub-departamento");
      return;
    }
    setSavingSubDept(true);
    try {
      const dept = departamentos.find((d) => d.id === selectedSubDeptDept);
      await axios.post(`${API}/encuestas/subdepartamentos`, {
        departamento_id: selectedSubDeptDept,
        departamento_nombre: dept?.name || "",
        nombre
      }, getAuthHeaders());
      toast.success("Sub-departamento creado");
      setNuevoSubDept("");
      fetchSubdepartamentos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear el sub-departamento");
    } finally {
      setSavingSubDept(false);
    }
  };

  const handleDeleteSubDept = async (subId) => {
    if (!window.confirm("¿Eliminar este sub-departamento?")) return;
    try {
      await axios.delete(`${API}/encuestas/subdepartamentos/${subId}`, getAuthHeaders());
      toast.success("Sub-departamento eliminado");
      fetchSubdepartamentos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleCrearTipoEncuesta = async () => {
    const nombre = nuevoTipoEncNombre.trim();
    if (!nombre) {
      toast.error("Ingrese el nombre del tipo de encuesta");
      return;
    }
    setSavingTipoEnc(true);
    try {
      await axios.post(`${API}/encuestas/tipos-encuesta`, { nombre, campo_fecha: nuevoTipoEncCampo }, getAuthHeaders());
      toast.success("Tipo de encuesta creado");
      setNuevoTipoEncNombre("");
      setNuevoTipoEncCampo("ninguno");
      fetchTiposEncuesta();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear el tipo de encuesta");
    } finally {
      setSavingTipoEnc(false);
    }
  };

  const handleUpdateTipoEncuestaCampo = async (tipo, campo_fecha) => {
    try {
      await axios.put(`${API}/encuestas/tipos-encuesta/${tipo.id}`, { nombre: tipo.nombre, campo_fecha }, getAuthHeaders());
      toast.success("Tipo de encuesta actualizado");
      fetchTiposEncuesta();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleDeleteTipoEncuesta = async (tipoId) => {
    if (!window.confirm("¿Eliminar este tipo de encuesta?")) return;
    try {
      await axios.delete(`${API}/encuestas/tipos-encuesta/${tipoId}`, getAuthHeaders());
      toast.success("Tipo de encuesta eliminado");
      fetchTiposEncuesta();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const getTiposForDept = (deptId) => {
    const cfg = tiposConfig.find((c) => c.departamento_id === deptId);
    return cfg?.tipos || [];
  };

  const handleSelectTipoDept = (deptId) => {
    setSelectedTipoDept(deptId);
    setTiposDraft(getTiposForDept(deptId));
    setNuevoTipo("");
  };

  const handleAddTipo = () => {
    const t = nuevoTipo.trim();
    if (!t) return;
    if (tiposDraft.some((x) => x.toLowerCase() === t.toLowerCase())) {
      toast.error("Ese tipo ya existe");
      return;
    }
    setTiposDraft([...tiposDraft, t]);
    setNuevoTipo("");
  };

  const handleRemoveTipo = (idx) => {
    setTiposDraft(tiposDraft.filter((_, i) => i !== idx));
  };

  const handleSaveTipos = async () => {
    if (!selectedTipoDept) {
      toast.error("Seleccione un departamento");
      return;
    }
    setSavingTipos(true);
    try {
      const dept = departamentos.find((d) => d.id === selectedTipoDept);
      await axios.put(
        `${API}/encuestas/tipos-departamento/${selectedTipoDept}`,
        { departamento_nombre: dept?.name || "", tipos: tiposDraft },
        getAuthHeaders()
      );
      toast.success("Tipos guardados correctamente");
      fetchTiposConfig();
    } catch (err) {
      toast.error("Error al guardar los tipos");
    } finally {
      setSavingTipos(false);
    }
  };

  const fetchPlantillas = async () => {
    try {
      const res = await axios.get(`${API}/encuestas/plantillas`, getAuthHeaders());
      setPlantillas(res.data);
    } catch (err) {
      toast.error("Error al cargar plantillas");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.nombre.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    if (!formData.tipo) {
      toast.error("Seleccione el tipo de encuesta");
      return;
    }
    if (formData.preguntas.length === 0) {
      toast.error("Debe agregar al menos una pregunta");
      return;
    }

    try {
      if (editingPlantilla) {
        await axios.put(`${API}/encuestas/plantillas/${editingPlantilla.id}`, formData, getAuthHeaders());
        toast.success("Plantilla actualizada");
      } else {
        await axios.post(`${API}/encuestas/plantillas`, formData, getAuthHeaders());
        toast.success("Plantilla creada");
      }
      setShowDialog(false);
      resetForm();
      fetchPlantillas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Está seguro de eliminar esta plantilla?")) return;
    try {
      await axios.delete(`${API}/encuestas/plantillas/${id}`, getAuthHeaders());
      toast.success("Plantilla eliminada");
      fetchPlantillas();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const handleClonar = async (plantilla) => {
    try {
      const res = await axios.post(`${API}/encuestas/plantillas/${plantilla.id}/clonar`, {}, getAuthHeaders());
      toast.success(`Plantilla "${res.data.nombre}" creada`);
      fetchPlantillas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al clonar plantilla");
    }
  };

  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingPlantilla, setViewingPlantilla] = useState(null);

  const openView = (plantilla) => {
    setViewingPlantilla(plantilla);
    setShowViewDialog(true);
  };

  const resetForm = () => {
    setFormData({
      nombre: "",
      descripcion: "",
      tipo: "",
      departamento_id: "",
      departamento_nombre: "",
      sub_departamento: "",
      mensaje_bienvenida: "Nos gustaría conocer su opinión sobre nuestro servicio.",
      mensaje_agradecimiento: "¡Gracias por su tiempo! Sus comentarios son muy valiosos para nosotros.",
      asunto_correo: "",
      mensaje_cuerpo_correo: "",
      preguntas: [],
      activa: true,
      logo_url: "",
      imagen_bienvenida_url: "",
      usar_imagen_bienvenida: false,
      color_primario: "#0C314C",
      color_secundario: "#45E69A",
      mostrar_logo_empresa: true,
      header_url: "",
      footer_url: "",
      color_texto_encabezado: "#FFFFFF",
      color_texto_cuerpo: "#374151",
      color_texto_footer: "#9ca3af",
      logo_alineacion: "centro",
      logo_tamano: "150"
    });
    setEditingPlantilla(null);
  };

  const openEdit = (plantilla) => {
    setEditingPlantilla(plantilla);
    setFormData({
      nombre: plantilla.nombre,
      descripcion: plantilla.descripcion || "",
      tipo: plantilla.tipo,
      departamento_id: plantilla.departamento_id || "",
      departamento_nombre: plantilla.departamento_nombre || "",
      sub_departamento: plantilla.sub_departamento || "",
      mensaje_bienvenida: plantilla.mensaje_bienvenida || "",
      mensaje_agradecimiento: plantilla.mensaje_agradecimiento || "",
      asunto_correo: plantilla.asunto_correo || "",
      mensaje_cuerpo_correo: plantilla.mensaje_cuerpo_correo || "",
      preguntas: plantilla.preguntas || [],
      activa: plantilla.activa,
      logo_url: plantilla.logo_url || "",
      imagen_bienvenida_url: plantilla.imagen_bienvenida_url || "",
      usar_imagen_bienvenida: plantilla.usar_imagen_bienvenida || false,
      color_primario: plantilla.color_primario || "#0C314C",
      color_secundario: plantilla.color_secundario || "#45E69A",
      mostrar_logo_empresa: plantilla.mostrar_logo_empresa !== false,
      header_url: plantilla.header_url || "",
      footer_url: plantilla.footer_url || "",
      color_texto_encabezado: plantilla.color_texto_encabezado || "#FFFFFF",
      color_texto_cuerpo: plantilla.color_texto_cuerpo || "#374151",
      color_texto_footer: plantilla.color_texto_footer || "#9ca3af",
      logo_alineacion: plantilla.logo_alineacion || "centro",
      logo_tamano: plantilla.logo_tamano || "150"
    });
    setShowDialog(true);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!editingPlantilla) {
      // Si es nueva plantilla, convertir a base64 localmente
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData({ ...formData, logo_url: event.target.result });
      };
      reader.readAsDataURL(file);
      return;
    }
    
    setUploadingLogo(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    
    try {
      const res = await axios.post(
        `${API}/encuestas/plantillas/${editingPlantilla.id}/subir-logo`,
        formDataUpload,
        { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" } }
      );
      setFormData({ ...formData, logo_url: res.data.logo_url });
      toast.success("Logo subido correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir logo");
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };

  const handleImagenBienvenidaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!editingPlantilla) {
      // Si es nueva plantilla, convertir a base64 localmente
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData({ ...formData, imagen_bienvenida_url: event.target.result, usar_imagen_bienvenida: true });
      };
      reader.readAsDataURL(file);
      return;
    }
    
    setUploadingImagenBienvenida(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    
    try {
      const res = await axios.post(
        `${API}/encuestas/plantillas/${editingPlantilla.id}/subir-imagen-bienvenida`,
        formDataUpload,
        { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" } }
      );
      setFormData({ ...formData, imagen_bienvenida_url: res.data.imagen_url, usar_imagen_bienvenida: true });
      toast.success("Imagen de bienvenida subida correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploadingImagenBienvenida(false);
      e.target.value = "";
    }
  };

  const [disenoDefaults, setDisenoDefaults] = useState({ header_url: null, footer_url: null });

  const fetchDisenoDefaults = async () => {
    try {
      const res = await axios.get(`${API}/encuestas/plantillas/diseno-defaults`, getAuthHeaders());
      setDisenoDefaults(res.data || { header_url: null, footer_url: null });
    } catch (err) {
      console.error("Error loading diseno defaults:", err);
    }
  };

  useEffect(() => {
    fetchDisenoDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHeaderFooterUpload = (e, campo) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error("La imagen no debe superar 1MB");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target.result;
      const eraDefault = disenoDefaults[campo === "header" ? "header_url" : "footer_url"] && disenoDefaults[campo === "header" ? "header_url" : "footer_url"] === formData[campo === "header" ? "header_url" : "footer_url"];
      setFormData((prev) => ({ ...prev, [campo === "header" ? "header_url" : "footer_url"]: dataUrl }));
      if (eraDefault) {
        await handleToggleDefault(campo, true, dataUrl);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleToggleDefault = async (campo, checked, urlOverride = null) => {
    const url = urlOverride || (campo === "header" ? formData.header_url : formData.footer_url);
    if (checked && !url) {
      toast.error("Primero suba una imagen");
      return;
    }
    try {
      const payload = campo === "header"
        ? { header_url: checked ? url : null, actualizar_header: true }
        : { footer_url: checked ? url : null, actualizar_footer: true };
      const res = await axios.put(`${API}/encuestas/plantillas/diseno-defaults`, payload, getAuthHeaders());
      setDisenoDefaults(res.data || { header_url: null, footer_url: null });
      toast.success(checked
        ? `${campo === "header" ? "Header" : "Footer"} establecido como predeterminado para todas las plantillas`
        : `${campo === "header" ? "Header" : "Footer"} predeterminado eliminado`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar predeterminado");
    }
  };

  const handleImportarPreguntas = async (e) => {
    const file = e.target.files[0];
    if (!file || !editingPlantilla) return;
    
    setUploadingPreguntas(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    
    try {
      const res = await axios.post(
        `${API}/encuestas/plantillas/${editingPlantilla.id}/importar-preguntas`,
        formDataUpload,
        { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${res.data.preguntas_importadas} preguntas importadas`);
      if (res.data.errores?.length > 0) {
        toast.warning(`${res.data.total_errores} errores encontrados`);
      }
      // Recargar la plantilla para obtener las preguntas actualizadas
      const plantillaActualizada = await axios.get(`${API}/encuestas/plantillas/${editingPlantilla.id}`, getAuthHeaders());
      setFormData({ ...formData, preguntas: plantillaActualizada.data.preguntas || [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar preguntas");
    } finally {
      setUploadingPreguntas(false);
      e.target.value = "";
    }
  };

  const openNewPregunta = () => {
    setEditingPregunta(null);
    setPreguntaForm({
      tipo: "nps",
      pregunta: "",
      opciones: [],
      escala_min: 1,
      escala_max: 10,
      obligatoria: true
    });
    setShowPreguntaDialog(true);
  };

  const openEditPregunta = (pregunta, index) => {
    setEditingPregunta(index);
    setPreguntaForm({
      tipo: pregunta.tipo,
      pregunta: pregunta.pregunta,
      opciones: pregunta.opciones || [],
      escala_min: pregunta.escala_min || 1,
      escala_max: pregunta.escala_max || 10,
      obligatoria: pregunta.obligatoria !== false
    });
    setShowPreguntaDialog(true);
  };

  const savePregunta = () => {
    if (!preguntaForm.pregunta.trim()) {
      toast.error("La pregunta es requerida");
      return;
    }
    
    if (preguntaForm.tipo === "opcion_multiple" && preguntaForm.opciones.length < 2) {
      toast.error("Agregue al menos 2 opciones");
      return;
    }
    
    if (preguntaForm.tipo === "seleccion_multiple" && preguntaForm.opciones.length < 2) {
      toast.error("Agregue al menos 2 opciones para selección múltiple");
      return;
    }

    const nuevaPregunta = {
      id: editingPregunta !== null ? formData.preguntas[editingPregunta].id : `p_${Date.now()}`,
      tipo: preguntaForm.tipo,
      pregunta: preguntaForm.pregunta,
      opciones: (preguntaForm.tipo === "opcion_multiple" || preguntaForm.tipo === "seleccion_multiple") ? preguntaForm.opciones : null,
      escala_min: preguntaForm.tipo === "escala" ? preguntaForm.escala_min : null,
      escala_max: preguntaForm.tipo === "escala" ? preguntaForm.escala_max : null,
      obligatoria: preguntaForm.obligatoria,
      orden: editingPregunta !== null ? editingPregunta : formData.preguntas.length
    };

    if (editingPregunta !== null) {
      const updated = [...formData.preguntas];
      updated[editingPregunta] = nuevaPregunta;
      setFormData({ ...formData, preguntas: updated });
    } else {
      setFormData({ ...formData, preguntas: [...formData.preguntas, nuevaPregunta] });
    }

    setShowPreguntaDialog(false);
  };

  const deletePregunta = (index) => {
    const updated = formData.preguntas.filter((_, i) => i !== index);
    setFormData({ ...formData, preguntas: updated });
  };

  const movePregunta = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= formData.preguntas.length) return;
    
    const updated = [...formData.preguntas];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setFormData({ ...formData, preguntas: updated });
  };

  const addOpcion = () => {
    if (!nuevaOpcion.trim()) return;
    setPreguntaForm({ 
      ...preguntaForm, 
      opciones: [...preguntaForm.opciones, nuevaOpcion.trim()] 
    });
    setNuevaOpcion("");
  };

  const removeOpcion = (index) => {
    setPreguntaForm({
      ...preguntaForm,
      opciones: preguntaForm.opciones.filter((_, i) => i !== index)
    });
  };

  const getTipoPreguntaInfo = (tipo) => {
    return TIPOS_PREGUNTA.find(t => t.id === tipo) || TIPOS_PREGUNTA[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-6 pt-14 md:pt-6 space-y-6" data-testid="encuestas-config-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Configuración de Encuestas
          </h1>
          <p className="text-muted-foreground">Cree y configure plantillas de encuestas NPS</p>
        </div>
        {activePageTab === "plantillas" && (
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="nueva-plantilla-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Plantilla
          </Button>
        )}
      </div>

      <Tabs value={activePageTab} onValueChange={setActivePageTab} className="w-full">
        <TabsList>
          <TabsTrigger value="plantillas" data-testid="tab-plantillas">Plantillas</TabsTrigger>
          <TabsTrigger value="tipos" data-testid="tab-tipos-departamento">Tipos por Departamento</TabsTrigger>
          <TabsTrigger value="subdepartamentos" data-testid="tab-subdepartamentos">Subdepartamentos</TabsTrigger>
          <TabsTrigger value="tipos-encuesta" data-testid="tab-tipos-encuesta">Tipos de Encuestas</TabsTrigger>
        </TabsList>

        <TabsContent value="plantillas" className="mt-4 space-y-6">
      {plantillas.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No hay plantillas de encuestas</h3>
            <p className="text-muted-foreground mb-4">Cree su primera plantilla para comenzar</p>
            <Button onClick={() => setShowDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Crear Plantilla
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {plantillas.map((plantilla) => (
            <Card key={plantilla.id} className={!plantilla.activa ? "opacity-60" : ""}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{plantilla.nombre}</CardTitle>
                    <CardDescription className="mt-1">
                      {tiposEncuesta.find(t => t.id === plantilla.tipo)?.nombre || TIPOS_ENCUESTA.find(t => t.id === plantilla.tipo)?.nombre || plantilla.tipo}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={plantilla.activa ? "default" : "secondary"}>
                      {plantilla.activa ? "Activa" : "Inactiva"}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openView(plantilla)}>
                          <Eye className="w-4 h-4 mr-2" />
                          Ver
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEdit(plantilla)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleClonar(plantilla)}>
                          <Copy className="w-4 h-4 mr-2" />
                          Clonar
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleDelete(plantilla.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {plantilla.descripcion || "Sin descripción"}
                </p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                  <FileText className="w-4 h-4" />
                  {plantilla.preguntas?.length || 0} preguntas
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openView(plantilla)}>
                    <Eye className="w-4 h-4 mr-1" />
                    Ver
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openEdit(plantilla)}>
                    <Pencil className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleClonar(plantilla)}>
                    <Copy className="w-4 h-4 mr-1" />
                    Clonar
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(plantilla.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
        </TabsContent>

        <TabsContent value="tipos" className="mt-4 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Tipos de Encuesta por Departamento
              </CardTitle>
              <CardDescription>
                Configure los tipos de encuesta disponibles para cada departamento. Estos tipos aparecerán en el selector "Tipo de Encuesta" al crear o editar una plantilla.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2 max-w-md">
                <Label>Departamento</Label>
                <Select value={selectedTipoDept || ""} onValueChange={handleSelectTipoDept}>
                  <SelectTrigger data-testid="tipos-dept-select">
                    <SelectValue placeholder="Seleccione un departamento..." />
                  </SelectTrigger>
                  <SelectContent>
                    {departamentos.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedTipoDept ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Agregar tipo de encuesta</Label>
                    <div className="flex gap-2 max-w-md">
                      <Input
                        value={nuevoTipo}
                        onChange={(e) => setNuevoTipo(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddTipo(); } }}
                        placeholder="Ej: Auto, Propiedades, Salud..."
                        data-testid="nuevo-tipo-input"
                      />
                      <Button type="button" variant="outline" onClick={handleAddTipo} data-testid="agregar-tipo-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        Agregar
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Tipos configurados ({tiposDraft.length})</Label>
                    {tiposDraft.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Aún no hay tipos configurados para este departamento.</p>
                    ) : (
                      <div className="space-y-2 max-w-md" data-testid="tipos-list">
                        {tiposDraft.map((t, idx) => (
                          <div key={`${t}-${idx}`} className="flex items-center justify-between p-2 border rounded-lg bg-muted/40">
                            <span className="text-sm font-medium">{t}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive"
                              onClick={() => handleRemoveTipo(idx)}
                              data-testid={`eliminar-tipo-${idx}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button onClick={handleSaveTipos} disabled={savingTipos} data-testid="guardar-tipos-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {savingTipos ? "Guardando..." : "Guardar cambios"}
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Seleccione un departamento para configurar sus tipos de encuesta.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subdepartamentos" className="mt-4 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Sub-departamentos
              </CardTitle>
              <CardDescription>
                Cree sub-departamentos para los departamentos existentes. Al asignarlos en la pestaña "Departamento" de una plantilla, los resultados se segmentarán por sub-departamento en la pestaña "Por Departamento" de Resultados.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2 max-w-md">
                <Label>Departamento</Label>
                <Select value={selectedSubDeptDept || ""} onValueChange={setSelectedSubDeptDept}>
                  <SelectTrigger data-testid="subdept-dept-select">
                    <SelectValue placeholder="Seleccione un departamento..." />
                  </SelectTrigger>
                  <SelectContent>
                    {departamentos.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedSubDeptDept ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Agregar sub-departamento</Label>
                    <div className="flex gap-2 max-w-md">
                      <Input
                        value={nuevoSubDept}
                        onChange={(e) => setNuevoSubDept(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCrearSubDept(); } }}
                        placeholder="Ej: Auto, Propiedades..."
                        data-testid="nuevo-subdept-input"
                      />
                      <Button type="button" variant="outline" onClick={handleCrearSubDept} disabled={savingSubDept} data-testid="agregar-subdept-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        {savingSubDept ? "Guardando..." : "Agregar"}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Sub-departamentos configurados ({getSubdeptsForDept(selectedSubDeptDept).length})</Label>
                    {getSubdeptsForDept(selectedSubDeptDept).length === 0 ? (
                      <p className="text-sm text-muted-foreground">Este departamento no tiene sub-departamentos.</p>
                    ) : (
                      <div className="space-y-2 max-w-md" data-testid="subdepts-list">
                        {getSubdeptsForDept(selectedSubDeptDept).map((s) => (
                          <div key={s.id} className="flex items-center justify-between p-2 border rounded-lg bg-muted/40">
                            <span className="text-sm font-medium">{s.nombre}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive"
                              onClick={() => handleDeleteSubDept(s.id)}
                              data-testid={`eliminar-subdept-${s.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Seleccione un departamento para gestionar sus sub-departamentos.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tipos-encuesta" className="mt-4 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Tipos de Encuestas
              </CardTitle>
              <CardDescription>
                Cree los tipos de encuesta y enlace a cada uno el campo de fecha que se pedirá al crear la campaña. Estos tipos aparecerán en el selector "Tipo de Encuesta" al crear o editar una plantilla.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Crear nuevo tipo */}
              <div className="space-y-2">
                <Label>Nuevo tipo de encuesta</Label>
                <div className="flex flex-col sm:flex-row gap-2 max-w-2xl">
                  <Input
                    value={nuevoTipoEncNombre}
                    onChange={(e) => setNuevoTipoEncNombre(e.target.value)}
                    placeholder="Nombre (ej: Renovación)"
                    className="flex-1"
                    data-testid="nuevo-tipo-encuesta-nombre"
                  />
                  <Select value={nuevoTipoEncCampo} onValueChange={setNuevoTipoEncCampo}>
                    <SelectTrigger className="sm:w-64" data-testid="nuevo-tipo-encuesta-campo">
                      <SelectValue placeholder="Campo de fecha" />
                    </SelectTrigger>
                    <SelectContent>
                      {CAMPOS_FECHA.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleCrearTipoEncuesta} disabled={savingTipoEnc} data-testid="crear-tipo-encuesta-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    {savingTipoEnc ? "Guardando..." : "Agregar"}
                  </Button>
                </div>
              </div>

              {/* Lista de tipos */}
              <div className="space-y-2">
                <Label>Tipos configurados ({tiposEncuesta.length})</Label>
                {tiposEncuesta.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aún no hay tipos de encuesta creados.</p>
                ) : (
                  <div className="space-y-2 max-w-2xl" data-testid="tipos-encuesta-list">
                    {tiposEncuesta.map((tipo) => (
                      <div key={tipo.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 border rounded-lg bg-muted/40">
                        <span className="font-medium flex-1">{tipo.nombre}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Campo de fecha:</span>
                          <Select value={tipo.campo_fecha || "ninguno"} onValueChange={(v) => handleUpdateTipoEncuestaCampo(tipo, v)}>
                            <SelectTrigger className="w-64" data-testid={`tipo-encuesta-campo-${tipo.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CAMPOS_FECHA.map((c) => (
                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDeleteTipoEncuesta(tipo.id)}
                            data-testid={`eliminar-tipo-encuesta-${tipo.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog para crear/editar plantilla */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPlantilla ? "Editar Plantilla" : "Nueva Plantilla de Encuesta"}
            </DialogTitle>
            <DialogDescription>
              Configure las preguntas y mensajes de la encuesta
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="departamento">Departamento</TabsTrigger>
              <TabsTrigger value="preguntas">Preguntas ({formData.preguntas.length})</TabsTrigger>
              <TabsTrigger value="diseno">Diseño</TabsTrigger>
              <TabsTrigger value="mensajes">Mensajes</TabsTrigger>
              <TabsTrigger value="importar">Importar</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre de la Encuesta *</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: Encuesta de Satisfacción Q1 2026"
                  data-testid="plantilla-nombre-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo de Encuesta *</Label>
                <Select value={formData.tipo || ""} onValueChange={(v) => setFormData({ ...formData, tipo: v })}>
                  <SelectTrigger data-testid="plantilla-tipo-select">
                    <SelectValue placeholder="Seleccione un tipo de encuesta" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposEncuesta.map((tipo) => (
                      <SelectItem key={tipo.id} value={tipo.id}>{tipo.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {tiposEncuesta.length === 0 && (
                  <p className="text-xs text-amber-600">No hay tipos de encuesta creados. Créelos en la pestaña "Tipos de Encuestas".</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="descripcion">Descripción</Label>
                <Textarea
                  id="descripcion"
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder="Descripción interna de la encuesta"
                  rows={3}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="activa">Encuesta Activa</Label>
                  <p className="text-sm text-muted-foreground">Las encuestas inactivas no pueden usarse en campañas</p>
                </div>
                <Switch
                  id="activa"
                  checked={formData.activa}
                  onCheckedChange={(v) => setFormData({ ...formData, activa: v })}
                />
              </div>
            </TabsContent>

            {/* Pestaña Departamento */}
            <TabsContent value="departamento" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Departamento</Label>
                <p className="text-xs text-muted-foreground">Seleccione a qué departamento pertenece esta encuesta</p>
                <Select 
                  value={formData.departamento_id || "none"} 
                  onValueChange={(v) => {
                    const actualValue = v === "none" ? "" : v;
                    const dept = departamentos.find(d => d.id === actualValue);
                    setFormData({ 
                      ...formData, 
                      departamento_id: actualValue,
                      departamento_nombre: dept?.name || "",
                      sub_departamento: "" // Resetear subdepartamento al cambiar
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar departamento..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin departamento</SelectItem>
                    {departamentos.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Selector de Sub-departamento (catálogo de la pestaña Subdepartamentos) */}
              {formData.departamento_id && getSubdeptsForDept(formData.departamento_id).length > 0 && (
                <div className="space-y-2 p-4 border rounded-lg bg-amber-50 dark:bg-amber-950">
                  <Label>Sub-departamento</Label>
                  <p className="text-xs text-muted-foreground">Segmenta los resultados de esta encuesta dentro del departamento</p>
                  <Select 
                    value={formData.sub_departamento || "none"} 
                    onValueChange={(v) => setFormData({ ...formData, sub_departamento: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="subdepartamento-select">
                      <SelectValue placeholder="Seleccionar sub-departamento..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin sub-departamento</SelectItem>
                      {getSubdeptsForDept(formData.departamento_id).map((s) => (
                        <SelectItem key={s.id} value={s.nombre}>{s.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Selector de Tipo de Encuesta según lo configurado por departamento */}
              {formData.departamento_id && getTiposForDept(formData.departamento_id).length > 0 && (
                <div className="space-y-2 p-4 border rounded-lg bg-blue-50 dark:bg-blue-950">
                  <Label>Tipo de Encuesta</Label>
                  <p className="text-xs text-muted-foreground">Seleccione el tipo específico para este departamento</p>
                  <Select 
                    value={formData.sub_departamento || "none"} 
                    onValueChange={(v) => setFormData({ ...formData, sub_departamento: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="tipo-encuesta-select">
                      <SelectValue placeholder="Seleccionar tipo..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin tipo específico</SelectItem>
                      {getTiposForDept(formData.departamento_id).map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {formData.departamento_nombre && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm">
                    <strong>Departamento seleccionado:</strong> {formData.departamento_nombre}
                    {formData.sub_departamento && ` - ${formData.sub_departamento}`}
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="preguntas" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  Agregue las preguntas que aparecerán en la encuesta
                </p>
                <Button onClick={openNewPregunta} size="sm" data-testid="agregar-pregunta-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar Pregunta
                </Button>
              </div>

              {formData.preguntas.length === 0 ? (
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <MessageSquare className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No hay preguntas configuradas</p>
                  <Button variant="outline" className="mt-3" onClick={openNewPregunta}>
                    Agregar primera pregunta
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {formData.preguntas.map((pregunta, index) => {
                    const tipoInfo = getTipoPreguntaInfo(pregunta.tipo);
                    const Icon = tipoInfo.icono;
                    return (
                      <div key={pregunta.id} className="flex items-center gap-2 p-3 border rounded-lg bg-card">
                        <div className="flex flex-col gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6"
                            onClick={() => movePregunta(index, -1)}
                            disabled={index === 0}
                          >
                            <ArrowUp className="w-3 h-3" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6"
                            onClick={() => movePregunta(index, 1)}
                            disabled={index === formData.preguntas.length - 1}
                          >
                            <ArrowDown className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className="w-4 h-4 text-muted-foreground" />
                            <Badge variant="outline" className="text-xs">{tipoInfo.nombre}</Badge>
                            {pregunta.obligatoria && <Badge variant="secondary" className="text-xs">Obligatoria</Badge>}
                          </div>
                          <p className="text-sm">{pregunta.pregunta}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditPregunta(pregunta, index)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => deletePregunta(index)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="mensajes" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="mensaje_bienvenida">Mensaje de Bienvenida</Label>
                <Textarea
                  id="mensaje_bienvenida"
                  value={formData.mensaje_bienvenida}
                  onChange={(e) => setFormData({ ...formData, mensaje_bienvenida: e.target.value })}
                  placeholder="Mensaje que verá el cliente al abrir la encuesta"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mensaje_agradecimiento">Mensaje de Agradecimiento</Label>
                <Textarea
                  id="mensaje_agradecimiento"
                  value={formData.mensaje_agradecimiento}
                  onChange={(e) => setFormData({ ...formData, mensaje_agradecimiento: e.target.value })}
                  placeholder="Mensaje que verá el cliente después de responder"
                  rows={3}
                />
              </div>

              <div className="border-t pt-4 space-y-4">
                <p className="text-sm font-medium text-muted-foreground">Correo de Invitación</p>
                <div className="space-y-2">
                  <Label htmlFor="asunto_correo">Asunto del Correo</Label>
                  <Input
                    id="asunto_correo"
                    data-testid="input-asunto-correo"
                    value={formData.asunto_correo}
                    onChange={(e) => setFormData({ ...formData, asunto_correo: e.target.value })}
                    placeholder="Te invitamos a compartir tu opinión"
                  />
                  <p className="text-xs text-muted-foreground">Asunto del correo de invitación. Si lo deja vacío, se usará el asunto configurado en el Portal Público.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mensaje_cuerpo_correo">Cuerpo del Correo</Label>
                  <Textarea
                    id="mensaje_cuerpo_correo"
                    data-testid="input-cuerpo-correo"
                    value={formData.mensaje_cuerpo_correo}
                    onChange={(e) => setFormData({ ...formData, mensaje_cuerpo_correo: e.target.value })}
                    placeholder="Tu feedback es muy importante para nosotros. Por favor, tómate unos minutos para responder esta breve encuesta."
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">Texto principal del correo de invitación (aparece después del saludo). Si lo deja vacío, se usará el texto configurado en el Portal Público.</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="diseno" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Logo de la Encuesta</Label>
                  <p className="text-sm text-muted-foreground">Suba un logo personalizado para esta encuesta (PNG, JPG, SVG - máx 500KB)</p>
                  <div className="flex items-center gap-4">
                    {formData.logo_url && (
                      <div className="w-24 h-24 border rounded-lg overflow-hidden flex items-center justify-center bg-white">
                        <img src={formData.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                      </div>
                    )}
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                        onChange={handleLogoUpload}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={uploadingLogo}
                      />
                      <Button variant="outline" disabled={uploadingLogo}>
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingLogo ? "Subiendo..." : "Subir Logo"}
                      </Button>
                    </div>
                    {formData.logo_url && (
                      <Button variant="ghost" size="sm" onClick={() => setFormData({ ...formData, logo_url: "" })}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="space-y-2">
                      <Label>Alineación del Logo</Label>
                      <Select value={formData.logo_alineacion} onValueChange={(v) => setFormData({ ...formData, logo_alineacion: v })}>
                        <SelectTrigger data-testid="logo-alineacion-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="izquierda">Izquierda</SelectItem>
                          <SelectItem value="centro">Centro</SelectItem>
                          <SelectItem value="derecha">Derecha</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tamaño del Logo ({formData.logo_tamano}px)</Label>
                      <input
                        type="range"
                        min="60"
                        max="400"
                        step="10"
                        value={parseInt(formData.logo_tamano) || 150}
                        onChange={(e) => setFormData({ ...formData, logo_tamano: e.target.value })}
                        className="w-full cursor-pointer"
                        data-testid="logo-tamano-slider"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Nota: si la plantilla tiene un header, el logo no se mostrará en el correo.</p>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="mostrar_logo_empresa">Mostrar Logo de la Empresa</Label>
                    <p className="text-sm text-muted-foreground">Mostrar el logo de QAsesores si no hay logo personalizado</p>
                  </div>
                  <Switch
                    id="mostrar_logo_empresa"
                    checked={formData.mostrar_logo_empresa}
                    onCheckedChange={(v) => setFormData({ ...formData, mostrar_logo_empresa: v })}
                  />
                </div>

                {/* Imagen de Bienvenida */}
                <div className="space-y-2 pt-4 border-t">
                  <Label>Imagen de Bienvenida</Label>
                  <p className="text-sm text-muted-foreground">Suba una imagen para mostrar en lugar del texto de bienvenida (PNG, JPG, GIF - máx 1MB)</p>
                  
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <Label htmlFor="usar_imagen_bienvenida">Usar Imagen de Bienvenida</Label>
                      <p className="text-xs text-muted-foreground">Si está activo, se mostrará la imagen en lugar del texto</p>
                    </div>
                    <Switch
                      id="usar_imagen_bienvenida"
                      checked={formData.usar_imagen_bienvenida}
                      onCheckedChange={(v) => setFormData({ ...formData, usar_imagen_bienvenida: v })}
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {formData.imagen_bienvenida_url && (
                      <div className="w-32 h-24 border rounded-lg overflow-hidden flex items-center justify-center bg-gray-100">
                        <img src={formData.imagen_bienvenida_url} alt="Imagen de bienvenida" className="max-w-full max-h-full object-cover" />
                      </div>
                    )}
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                        onChange={handleImagenBienvenidaUpload}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={uploadingImagenBienvenida}
                      />
                      <Button variant="outline" disabled={uploadingImagenBienvenida}>
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingImagenBienvenida ? "Subiendo..." : "Subir Imagen"}
                      </Button>
                    </div>
                    {formData.imagen_bienvenida_url && (
                      <Button variant="ghost" size="sm" onClick={() => setFormData({ ...formData, imagen_bienvenida_url: "", usar_imagen_bienvenida: false })}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Header del Correo */}
                <div className="space-y-2 pt-4 border-t">
                  <Label>Header del Correo</Label>
                  <p className="text-sm text-muted-foreground">Imagen que reemplaza todo el encabezado del correo (PNG, JPG - máx 1MB). Se recomienda 600px de ancho.</p>
                  <div className="flex items-center gap-4">
                    {formData.header_url && (
                      <div className="w-40 h-16 border rounded-lg overflow-hidden flex items-center justify-center bg-gray-100">
                        <img src={formData.header_url} alt="Header" className="max-w-full max-h-full object-contain" />
                      </div>
                    )}
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={(e) => handleHeaderFooterUpload(e, "header")}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        data-testid="header-upload-input"
                      />
                      <Button variant="outline" data-testid="header-upload-btn">
                        <Upload className="w-4 h-4 mr-2" />
                        Subir Header
                      </Button>
                    </div>
                    {formData.header_url && (
                      <Button variant="ghost" size="sm" data-testid="header-delete-btn" onClick={() => setFormData({ ...formData, header_url: "" })}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="header_default">Predeterminado para todas las plantillas</Label>
                      <p className="text-xs text-muted-foreground">Las plantillas sin header propio usarán este header</p>
                    </div>
                    <Switch
                      id="header_default"
                      data-testid="header-default-switch"
                      checked={!!disenoDefaults.header_url && disenoDefaults.header_url === formData.header_url}
                      onCheckedChange={(v) => handleToggleDefault("header", v)}
                    />
                  </div>
                  {disenoDefaults.header_url && disenoDefaults.header_url !== formData.header_url && (
                    <p className="text-xs text-amber-600">Existe un header predeterminado configurado que se usará si esta plantilla no tiene uno propio.</p>
                  )}
                </div>

                {/* Footer del Correo */}
                <div className="space-y-2 pt-4 border-t">
                  <Label>Footer del Correo</Label>
                  <p className="text-sm text-muted-foreground">Imagen que reemplaza el pie del correo (PNG, JPG - máx 1MB). Se recomienda 600px de ancho.</p>
                  <div className="flex items-center gap-4">
                    {formData.footer_url && (
                      <div className="w-40 h-16 border rounded-lg overflow-hidden flex items-center justify-center bg-gray-100">
                        <img src={formData.footer_url} alt="Footer" className="max-w-full max-h-full object-contain" />
                      </div>
                    )}
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={(e) => handleHeaderFooterUpload(e, "footer")}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        data-testid="footer-upload-input"
                      />
                      <Button variant="outline" data-testid="footer-upload-btn">
                        <Upload className="w-4 h-4 mr-2" />
                        Subir Footer
                      </Button>
                    </div>
                    {formData.footer_url && (
                      <Button variant="ghost" size="sm" data-testid="footer-delete-btn" onClick={() => setFormData({ ...formData, footer_url: "" })}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="footer_default">Predeterminado para todas las plantillas</Label>
                      <p className="text-xs text-muted-foreground">Las plantillas sin footer propio usarán este footer</p>
                    </div>
                    <Switch
                      id="footer_default"
                      data-testid="footer-default-switch"
                      checked={!!disenoDefaults.footer_url && disenoDefaults.footer_url === formData.footer_url}
                      onCheckedChange={(v) => handleToggleDefault("footer", v)}
                    />
                  </div>
                  {disenoDefaults.footer_url && disenoDefaults.footer_url !== formData.footer_url && (
                    <p className="text-xs text-amber-600">Existe un footer predeterminado configurado que se usará si esta plantilla no tiene uno propio.</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="color_primario">Color Primario</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="color_primario"
                        value={formData.color_primario}
                        onChange={(e) => setFormData({ ...formData, color_primario: e.target.value })}
                        className="w-12 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={formData.color_primario}
                        onChange={(e) => setFormData({ ...formData, color_primario: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="color_secundario">Color Secundario</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="color_secundario"
                        value={formData.color_secundario}
                        onChange={(e) => setFormData({ ...formData, color_secundario: e.target.value })}
                        className="w-12 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={formData.color_secundario}
                        onChange={(e) => setFormData({ ...formData, color_secundario: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="color_texto_encabezado">Texto Encabezado</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="color_texto_encabezado"
                        data-testid="color-texto-encabezado-input"
                        value={formData.color_texto_encabezado}
                        onChange={(e) => setFormData({ ...formData, color_texto_encabezado: e.target.value })}
                        className="w-12 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={formData.color_texto_encabezado}
                        onChange={(e) => setFormData({ ...formData, color_texto_encabezado: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="color_texto_cuerpo">Texto Cuerpo</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="color_texto_cuerpo"
                        data-testid="color-texto-cuerpo-input"
                        value={formData.color_texto_cuerpo}
                        onChange={(e) => setFormData({ ...formData, color_texto_cuerpo: e.target.value })}
                        className="w-12 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={formData.color_texto_cuerpo}
                        onChange={(e) => setFormData({ ...formData, color_texto_cuerpo: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="color_texto_footer">Texto Footer</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="color_texto_footer"
                        data-testid="color-texto-footer-input"
                        value={formData.color_texto_footer}
                        onChange={(e) => setFormData({ ...formData, color_texto_footer: e.target.value })}
                        className="w-12 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={formData.color_texto_footer}
                        onChange={(e) => setFormData({ ...formData, color_texto_footer: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Vista previa del correo al cliente */}
                <div className="space-y-2">
                  <Label>Vista Previa del Correo al Cliente</Label>
                  <p className="text-sm text-muted-foreground">Así recibirá el cliente el correo de invitación a la encuesta</p>
                  {emailPreviewAsunto && (
                    <div className="text-sm bg-slate-100 dark:bg-slate-800 rounded-md px-3 py-2" data-testid="email-preview-asunto">
                      <span className="font-semibold">Asunto:</span> {emailPreviewAsunto}
                    </div>
                  )}
                  <div className="border rounded-lg overflow-hidden bg-white relative" data-testid="email-preview-container">
                    {loadingEmailPreview && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
                        <p className="text-sm text-slate-500">Actualizando vista previa...</p>
                      </div>
                    )}
                    {emailPreviewHtml ? (
                      <iframe
                        title="Vista previa del correo"
                        srcDoc={emailPreviewHtml}
                        className="w-full"
                        style={{ height: "540px", border: "none" }}
                        sandbox=""
                        data-testid="email-preview-iframe"
                      />
                    ) : (
                      <div className="h-40 flex items-center justify-center text-sm text-slate-400">
                        Cargando vista previa del correo...
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Input
                      type="email"
                      placeholder="correo@ejemplo.com"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      className="flex-1"
                      data-testid="test-email-input"
                    />
                    <Button
                      variant="outline"
                      onClick={handleEnviarCorreoPrueba}
                      disabled={sendingTestEmail}
                      data-testid="enviar-correo-prueba-btn"
                    >
                      {sendingTestEmail ? "Enviando..." : "Enviar correo de prueba"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Envíe el correo de la vista previa a su email para verificarlo antes de lanzar la campaña.</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="importar" className="space-y-4 mt-4">
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Importar Preguntas desde Excel</CardTitle>
                    <CardDescription>
                      Cargue un archivo Excel con las preguntas de la encuesta
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!editingPlantilla ? (
                      <div className="text-center py-4 text-muted-foreground">
                        <p>Guarde primero la plantilla para poder importar preguntas</p>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleImportarPreguntas}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            disabled={uploadingPreguntas}
                          />
                          <Button variant="outline" className="w-full" disabled={uploadingPreguntas}>
                            <Upload className="w-4 h-4 mr-2" />
                            {uploadingPreguntas ? "Importando..." : "Seleccionar Archivo Excel"}
                          </Button>
                        </div>
                        
                        <div className="bg-muted p-4 rounded-lg text-sm">
                          <p className="font-medium mb-2">Formato del archivo:</p>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-1">Columna</th>
                                <th className="text-left py-1">Descripción</th>
                                <th className="text-left py-1">Requerido</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr><td className="py-1 font-mono">tipo</td><td>nps, satisfaccion, texto, opcion_multiple, escala</td><td>Sí</td></tr>
                              <tr><td className="py-1 font-mono">pregunta</td><td>Texto de la pregunta</td><td>Sí</td></tr>
                              <tr><td className="py-1 font-mono">obligatoria</td><td>si/no</td><td>No</td></tr>
                              <tr><td className="py-1 font-mono">opciones</td><td>Opciones separadas por | (para opcion_multiple)</td><td>No</td></tr>
                              <tr><td className="py-1 font-mono">escala_min</td><td>Valor mínimo (para escala)</td><td>No</td></tr>
                              <tr><td className="py-1 font-mono">escala_max</td><td>Valor máximo (para escala)</td><td>No</td></tr>
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} data-testid="guardar-plantilla-btn">
              <Save className="w-4 h-4 mr-2" />
              {editingPlantilla ? "Actualizar" : "Crear"} Plantilla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para crear/editar pregunta */}
      <Dialog open={showPreguntaDialog} onOpenChange={setShowPreguntaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingPregunta !== null ? "Editar Pregunta" : "Nueva Pregunta"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo de Pregunta</Label>
              <Select value={preguntaForm.tipo} onValueChange={(v) => setPreguntaForm({ ...preguntaForm, tipo: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_PREGUNTA.map((tipo) => {
                    const Icon = tipo.icono;
                    return (
                      <SelectItem key={tipo.id} value={tipo.id}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          <span>{tipo.nombre}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {getTipoPreguntaInfo(preguntaForm.tipo).descripcion}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pregunta">Texto de la Pregunta *</Label>
              <Textarea
                id="pregunta"
                value={preguntaForm.pregunta}
                onChange={(e) => setPreguntaForm({ ...preguntaForm, pregunta: e.target.value })}
                placeholder={
                  preguntaForm.tipo === "nps" 
                    ? "Ej: Del 0 al 10, ¿qué tan probable es que nos recomiende a un amigo o colega?"
                    : "Escriba la pregunta"
                }
                rows={2}
                data-testid="pregunta-texto-input"
              />
            </div>

            {preguntaForm.tipo === "opcion_multiple" && (
              <div className="space-y-2">
                <Label>Opciones de Respuesta (selección única)</Label>
                <div className="flex gap-2">
                  <Input
                    value={nuevaOpcion}
                    onChange={(e) => setNuevaOpcion(e.target.value)}
                    placeholder="Nueva opción"
                    onKeyPress={(e) => e.key === 'Enter' && addOpcion()}
                  />
                  <Button type="button" variant="outline" onClick={addOpcion}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {preguntaForm.opciones.map((op, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">
                      {op}
                      <button onClick={() => removeOpcion(i)} className="ml-1 hover:text-destructive">×</button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {preguntaForm.tipo === "seleccion_multiple" && (
              <div className="space-y-2">
                <Label>Opciones de Respuesta (selección múltiple - checkboxes)</Label>
                <p className="text-xs text-muted-foreground">El encuestado podrá seleccionar más de una opción</p>
                <div className="flex gap-2">
                  <Input
                    value={nuevaOpcion}
                    onChange={(e) => setNuevaOpcion(e.target.value)}
                    placeholder="Nueva opción"
                    onKeyPress={(e) => e.key === 'Enter' && addOpcion()}
                  />
                  <Button type="button" variant="outline" onClick={addOpcion}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {preguntaForm.opciones.map((op, i) => (
                    <Badge key={i} variant="outline" className="gap-1 bg-blue-50">
                      <CheckSquare className="w-3 h-3" />
                      {op}
                      <button onClick={() => removeOpcion(i)} className="ml-1 hover:text-destructive">×</button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {preguntaForm.tipo === "escala" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="escala_min">Valor Mínimo</Label>
                  <Input
                    id="escala_min"
                    type="number"
                    value={preguntaForm.escala_min}
                    onChange={(e) => setPreguntaForm({ ...preguntaForm, escala_min: parseInt(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="escala_max">Valor Máximo</Label>
                  <Input
                    id="escala_max"
                    type="number"
                    value={preguntaForm.escala_max}
                    onChange={(e) => setPreguntaForm({ ...preguntaForm, escala_max: parseInt(e.target.value) })}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label htmlFor="obligatoria">Respuesta Obligatoria</Label>
              <Switch
                id="obligatoria"
                checked={preguntaForm.obligatoria}
                onCheckedChange={(v) => setPreguntaForm({ ...preguntaForm, obligatoria: v })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreguntaDialog(false)}>Cancelar</Button>
            <Button onClick={savePregunta} data-testid="guardar-pregunta-btn">
              <Save className="w-4 h-4 mr-2" />
              {editingPregunta !== null ? "Actualizar" : "Agregar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para Ver Plantilla */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {viewingPlantilla?.nombre}
            </DialogTitle>
            <DialogDescription>
              Vista detallada de la plantilla de encuesta
            </DialogDescription>
          </DialogHeader>
          
          {viewingPlantilla && (
            <div className="space-y-6">
              {/* Info General */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground text-xs">Tipo</Label>
                  <p className="font-medium">{tiposEncuesta.find(t => t.id === viewingPlantilla.tipo)?.nombre || TIPOS_ENCUESTA.find(t => t.id === viewingPlantilla.tipo)?.nombre || viewingPlantilla.tipo}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Estado</Label>
                  <Badge variant={viewingPlantilla.activa ? "default" : "secondary"}>
                    {viewingPlantilla.activa ? "Activa" : "Inactiva"}
                  </Badge>
                </div>
              </div>
              
              {viewingPlantilla.descripcion && (
                <div>
                  <Label className="text-muted-foreground text-xs">Descripción</Label>
                  <p className="text-sm">{viewingPlantilla.descripcion}</p>
                </div>
              )}

              {/* Mensajes */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm">Mensajes</h4>
                <div className="p-3 bg-muted rounded-lg">
                  <Label className="text-muted-foreground text-xs">Mensaje de Bienvenida</Label>
                  <p className="text-sm">{viewingPlantilla.mensaje_bienvenida}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <Label className="text-muted-foreground text-xs">Mensaje de Agradecimiento</Label>
                  <p className="text-sm">{viewingPlantilla.mensaje_agradecimiento}</p>
                </div>
              </div>

              {/* Preguntas */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Preguntas ({viewingPlantilla.preguntas?.length || 0})
                </h4>
                <div className="space-y-2">
                  {viewingPlantilla.preguntas?.map((pregunta, index) => {
                    const tipoInfo = TIPOS_PREGUNTA.find(t => t.id === pregunta.tipo);
                    const Icon = tipoInfo?.icono || Hash;
                    return (
                      <div key={pregunta.id || index} className="p-3 border rounded-lg">
                        <div className="flex items-start gap-3">
                          <div className="flex items-center justify-center w-6 h-6 rounded bg-primary/10 text-primary text-xs font-bold">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{pregunta.pregunta}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                <Icon className="w-3 h-3 mr-1" />
                                {tipoInfo?.nombre || pregunta.tipo}
                              </Badge>
                              {pregunta.obligatoria && (
                                <Badge variant="secondary" className="text-xs">Obligatoria</Badge>
                              )}
                            </div>
                            {pregunta.tipo === "opcion_multiple" && pregunta.opciones?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {pregunta.opciones.map((op, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">{op}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Colores */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm">Personalización Visual</h4>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded border" 
                      style={{ backgroundColor: viewingPlantilla.color_primario || "#0C314C" }}
                    />
                    <span className="text-sm">Primario</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded border" 
                      style={{ backgroundColor: viewingPlantilla.color_secundario || "#45E69A" }}
                    />
                    <span className="text-sm">Secundario</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Cerrar
            </Button>
            <Button onClick={() => { setShowViewDialog(false); openEdit(viewingPlantilla); }}>
              <Pencil className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </main>
    </div>
  );
}
